/**
 * Payment types.
 *
 * A payment record is **not an invoice**: no number, no tax identifiers, no
 * VAT breakdown, no rendered document. That boundary is what keeps Alchely
 * out of Verifactu scope — see `backend/app/domain/models/payment.py`. The
 * therapist issues their real invoice in their own billing software and
 * records its reference in `external_invoice_ref`.
 *
 * Money arrives from the API as decimal *strings*, not numbers, and stays that
 * way here. A JS number cannot hold 0.1 + 0.2 exactly, and a total that is a
 * cent out is a real problem for the person paying it. Amounts are parsed only
 * at the edges — once for display, once when building a request — and never
 * accumulated in floating point.
 */

export type PaymentStatus = "draft" | "pending" | "paid" | "cancelled";

export const PAYMENT_STATUSES: PaymentStatus[] = [
    "draft",
    "pending",
    "paid",
    "cancelled",
];

/** A decimal amount in wire format, e.g. "60.00". */
export type Money = string;

export interface PaymentLineItem {
    id: string;
    description: string;
    quantity: Money;
    unit_price: Money;
    subtotal: Money;
}

/** The ways a payment reminder can reach the patient. */
export type ReminderChannel = "email" | "whatsapp";

/** One nudge that actually went out. */
export interface PaymentReminder {
    sent_at: string;
    /** Null for a reminder recorded before the channel was tracked. */
    channel: ReminderChannel | null;
}

export interface PaymentLineItemInput {
    description: string;
    quantity: Money;
    unit_price: Money;
}

export interface Payment {
    id: string;
    status: PaymentStatus;
    patient_id: string;
    /** Read through the relation server-side, never stored on the payment.
     *  Null only if the patient has since been erased. */
    patient_name: string | null;
    session_id: string | null;
    line_items: PaymentLineItem[];
    sent_at: string | null;
    due_date: string | null;
    notes: string | null;
    /** The therapist's own invoice number, from their own billing software.
     *  Typed by hand, never generated here. */
    external_invoice_ref: string | null;
    stripe_payment_link_url: string | null;
    total: Money;
    /** Payment chasing: how many nudges have gone, and whether another may. */
    reminder_count: number;
    max_reminders: number;
    last_reminder_sent_at: string | null;
    /** How the last reminder actually went out. The therapist does not pick
     *  the channel — the patient's WhatsApp consent decides it — so this is
     *  the only way to answer "we sent it, but where?". */
    last_reminder_channel: ReminderChannel | null;
    /** The full log, oldest first. `reminder_count` is its length. */
    reminders: PaymentReminder[];
    can_send_reminder: boolean;
    created_at: string;
    updated_at: string;
}

export interface PaymentListPage {
    items: Payment[];
    total: number;
    offset: number;
    limit: number;
}

export interface PaymentFilters {
    status?: PaymentStatus;
    patientId?: string;
    sessionId?: string;
    fromDate?: string;
    toDate?: string;
    offset?: number;
    limit?: number;
}

export interface PaymentCreateInput {
    patient_id: string;
    session_id?: string | null;
    line_items: PaymentLineItemInput[];
    due_date?: string | null;
    notes?: string | null;
    external_invoice_ref?: string | null;
}

export interface PaymentUpdateInput {
    line_items?: PaymentLineItemInput[];
    due_date?: string | null;
    notes?: string | null;
    external_invoice_ref?: string | null;
}

export interface PaymentSendInput {
    sent_at: string;
    due_date?: string | null;
    request_payment_link?: boolean;
}

/**
 * Who a payment for one patient is charged through.
 *
 * `managed_by_center`: the patient belongs to the therapist's centre, which
 * charges them — its Stripe account, its defaults. The therapist's own
 * patients are charged through the therapist. `stripe_connected` is about
 * whichever account that is.
 */
export interface PaymentAccount {
    managed_by_center: boolean;
    center_name: string | null;
    stripe_connected: boolean;
    default_payment_concept: string | null;
    default_payment_amount: string | null;
}

/**
 * A session a payment can be attached to — only what the picker shows.
 *
 * Payments reads sessions through its own port rather than session's API:
 * the session page embeds payment widgets, so payments depending back on
 * session would tie the two modules into a cycle.
 */
export interface PayableSession {
    id: string;
    title: string;
    date: string;
}

export interface PayableSessionsPage {
    sessions: PayableSession[];
    total: number;
}

export interface StripeConnectStatus {
    connected: boolean;
    account_display_name: string | null;
    /** False when the deployment has no Stripe credentials at all. */
    available: boolean;
}

/** A blank charged line, ready to be filled in. */
export const emptyLineItem = (): PaymentLineItemInput => ({
    description: "",
    quantity: "1",
    unit_price: "0.00",
});

/**
 * The line a new payment starts with, given the therapist's saved default.
 *
 * The two halves are independent: a therapist who varies the price but never
 * the wording still gets the concept filled in, and vice versa.
 */
export const defaultLineItem = (
    concept?: string | null,
    amount?: string | null
): PaymentLineItemInput => ({
    ...emptyLineItem(),
    ...(concept ? { description: concept } : {}),
    ...(amount ? { unit_price: Number(amount).toFixed(2) } : {}),
});

/** A line the therapist has not touched yet — safe to overwrite with a default. */
export const isPristineLineItem = (item: PaymentLineItemInput): boolean =>
    item.description === "" && item.quantity === "1" && item.unit_price === "0.00";

export const isEditable = (payment: Payment): boolean =>
    payment.status === "draft";

export const canSend = (payment: Payment): boolean => payment.status === "draft";

export const canMarkPaid = (payment: Payment): boolean =>
    payment.status === "pending";

/** A paid payment cannot be cancelled — the money already moved. */
export const canCancel = (payment: Payment): boolean =>
    payment.status === "draft" || payment.status === "pending";

/**
 * Whether to offer the manual reminder button.
 *
 * The server already decided — it knows the cap and the payment state — so
 * this reads its answer rather than re-deriving it and risking a button that
 * disagrees with the endpoint behind it.
 */
export const canSendReminder = (payment: Payment): boolean =>
    payment.can_send_reminder;

export const remindersRemaining = (payment: Payment): number =>
    Math.max(0, payment.max_reminders - payment.reminder_count);

/**
 * Parse a wire amount, or null if it is not a number.
 *
 * `Number("")` is 0, not NaN — so a blank field would silently render as
 * "0,00 €" and read as a real amount of zero rather than a missing one.
 * Blank is rejected here so every caller gets that right for free.
 */
export const parseAmount = (value: Money | null | undefined): number | null => {
    if (value === null || value === undefined) return null;
    if (String(value).trim() === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Format a wire amount for display.
 *
 * Parsing happens here and nowhere else: this is the one place a decimal
 * string becomes a number, and it is immediately handed to the formatter
 * rather than kept around to be added to something.
 */
export const formatMoney = (amount: Money, locale = "es-ES"): string => {
    const value = parseAmount(amount);
    if (value === null) return "—";
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
    }).format(value);
};

/**
 * Sum line items without going through floating point.
 *
 * Used for the live total while a draft is being edited, before the server
 * has recomputed it. Works in integer cents so the preview agrees with the
 * figure the backend will produce.
 */
export const previewTotal = (items: PaymentLineItemInput[]): Money => {
    let cents = 0;

    for (const item of items) {
        const quantity = parseAmount(item.quantity);
        const unitPrice = parseAmount(item.unit_price);
        if (quantity === null || unitPrice === null) continue;

        cents += Math.round(quantity * unitPrice * 100);
    }

    return (cents / 100).toFixed(2);
};


/**
 * Why a send failed, in terms the therapist can act on.
 *
 * The server answers with a code rather than a sentence, so the wording stays
 * here and stays translated. `unknown` is the honest fallback: a network drop
 * and an unhandled 500 are indistinguishable from the client, and pretending
 * otherwise would put a confident wrong explanation in front of someone
 * chasing money.
 */
export type SendFailure =
    | "stripe_not_connected"
    | "center_stripe_not_connected"
    | "payment_link_needs_amount"
    | "stripe_unavailable"
    | "conflict"
    | "unknown";

interface ApiErrorShape {
    response?: {
        status?: number;
        data?: { detail?: string | { code?: string } };
    };
}

export const sendFailureFrom = (error: unknown): SendFailure => {
    const response = (error as ApiErrorShape)?.response;
    const detail = response?.data?.detail;
    const code = typeof detail === "object" ? detail?.code : undefined;

    if (code === "stripe_not_connected") return "stripe_not_connected";
    // The centre charges this patient and has no Stripe yet: not something
    // the therapist can fix from their own settings.
    if (code === "center_stripe_not_connected") return "center_stripe_not_connected";
    if (code === "payment_link_needs_amount") return "payment_link_needs_amount";
    // 502 is the provider being unreachable or unhappy — not the therapist's
    // mistake, and worth retrying, unlike everything else here.
    if (response?.status === 502) return "stripe_unavailable";
    // Someone else already sent or cancelled it in another tab.
    if (response?.status === 409) return "conflict";
    return "unknown";
};

/** Whether retrying the same request could plausibly work. */
export const isRetryable = (failure: SendFailure): boolean =>
    failure === "stripe_unavailable" || failure === "unknown";


/**
 * Why a reminder could not be sent.
 *
 * Two causes needing two different people: `no_contact` is the therapist's to
 * fix on the patient record, `not_configured` needs an operator. Telling them
 * apart is the difference between a fixable problem and a shrug.
 */
export type ReminderFailure =
    | "no_contact"
    | "not_configured"
    | "provider_error"
    | "no_recipient"
    | "cap_reached"
    | "unknown";

export const reminderFailureFrom = (error: unknown): ReminderFailure => {
    const response = (error as ApiErrorShape)?.response;
    const detail = response?.data?.detail;
    const code = typeof detail === "object" ? detail?.code : undefined;

    if (code === "reminder_no_contact") return "no_contact";
    if (code === "reminder_not_configured") return "not_configured";
    if (code === "reminder_provider_error") return "provider_error";
    // The therapist's own settings keep reminders away from the patient.
    if (code === "reminder_no_recipient") return "no_recipient";
    // The aggregate refuses a fourth reminder, or a second one today.
    if (response?.status === 409) return "cap_reached";
    return "unknown";
};
